import { useCorpusDeletionPreview, useDeleteCorpusDocuments } from "@noa/api-client";
import type { DeletionPreview, DeletionPreviewRequest } from "@noa/api-core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Descriptions, Dialog, message, Spin, Tag, Text } from "~/components/ui";
import { corpusProblem, type DescribedFilter, parseLocalDay, sourceLabelKey } from "~/utils/corpus-admin";
import { CorpusErrorAlert } from "./corpus-error-alert";

/** What the admin chose to delete, as the dialog recalls it. */
export type DeletionSelection =
  { kind: "document"; name: string; sourceUrl: string } | { kind: "filters"; filters: DescribedFilter[] };

/** A delete to preview: the request the API takes, and the selection it stands for. */
export interface CorpusDeletion {
  request: DeletionPreviewRequest;
  selection: DeletionSelection;
}

interface CorpusDeletionDialogProps {
  /** null when the dialog is closed. */
  deletion: CorpusDeletion | null;
  onClose: () => void;
}

function SelectionSummary({ selection }: { selection: DeletionSelection }) {
  const { t, i18n } = useTranslation("admin");

  if (selection.kind === "document") {
    return (
      <div className="mb-3 flex flex-col">
        <Text strong className="break-words">
          {selection.name}
        </Text>
        <Text tone="muted" className="break-all text-xs">
          {selection.sourceUrl}
        </Text>
      </div>
    );
  }

  const label = (filter: DescribedFilter) =>
    filter.kind === "field" ? filter.field : t(`vectorstoreAdmin.deletion.filters.${filter.kind}`);
  const value = (filter: DescribedFilter) => {
    if (filter.kind === "source") return t(sourceLabelKey(filter.value), { defaultValue: filter.value });
    if (filter.kind === "from" || filter.kind === "to") {
      return parseLocalDay(filter.value).toLocaleDateString(i18n.language);
    }
    return filter.value;
  };

  return (
    <div className="mb-3 flex flex-wrap items-center gap-1">
      <Text tone="muted" className="mr-1">
        {t("vectorstoreAdmin.deletion.selection")}
      </Text>
      {selection.filters.map((filter) => (
        <Tag key={filter.kind === "field" ? `field:${filter.field}` : filter.kind}>
          {label(filter)} <Text strong>{value(filter)}</Text>
        </Tag>
      ))}
    </div>
  );
}

function PreviewSummary({ preview }: { preview: DeletionPreview }) {
  const { t, i18n } = useTranslation("admin");
  const formatNumber = (value: number) => value.toLocaleString(i18n.language);
  const byType = [
    ...preview.documents_by_type,
    ...(preview.untyped_documents > 0
      ? [{ value: t("vectorstoreAdmin.overview.untyped"), count: preview.untyped_documents }]
      : []),
  ];

  return (
    <div className="flex flex-col gap-3">
      <Descriptions
        bordered
        size="small"
        column={1}
        items={[
          {
            key: "documents",
            label: t("vectorstoreAdmin.deletion.documents"),
            children: formatNumber(preview.source_urls.length),
          },
          { key: "records", label: t("vectorstoreAdmin.deletion.records"), children: formatNumber(preview.documents) },
          { key: "chunks", label: t("vectorstoreAdmin.deletion.chunks"), children: formatNumber(preview.chunks) },
          byType.length > 0 && {
            key: "byType",
            label: t("vectorstoreAdmin.deletion.byType"),
            children: (
              <div className="flex flex-wrap gap-1">
                {byType.map((count) => (
                  <Tag key={count.value}>
                    {count.value} <Text strong>{formatNumber(count.count)}</Text>
                  </Tag>
                ))}
              </div>
            ),
          },
        ]}
      />
      {preview.unknown_urls.length > 0 && (
        <Alert
          type="info"
          showIcon
          message={t("vectorstoreAdmin.deletion.unknown", { count: preview.unknown_urls.length })}
        />
      )}
      <Alert type="warning" showIcon message={t("vectorstoreAdmin.deletion.warning")} />
    </div>
  );
}

/** Shows what deleting would remove, then deletes exactly the previewed documents. */
export function CorpusDeletionDialog({ deletion, onClose }: CorpusDeletionDialogProps) {
  const { t, i18n } = useTranslation("admin");
  // The last deletion stays shown while the dialog fades out
  const [shown, setShown] = useState<CorpusDeletion | null>(null);
  if (deletion !== null && deletion !== shown) {
    setShown(deletion);
  }
  const { preview, isLoading, isError, error } = useCorpusDeletionPreview(shown?.request ?? null);
  const { mutate: deleteDocuments, isPending } = useDeleteCorpusDocuments();

  const urls = preview?.source_urls ?? [];
  const formatNumber = (value: number) => value.toLocaleString(i18n.language);

  const handleDelete = () => {
    deleteDocuments(urls, {
      onSuccess: (result) => {
        message.success(
          t("vectorstoreAdmin.deletion.done", {
            documents: formatNumber(result.documents_removed),
            chunks: formatNumber(result.chunks_removed),
          })
        );
        onClose();
      },
      onError: (deleteError) => {
        const problem = corpusProblem(deleteError);
        message.error(
          problem?.code === "too_many"
            ? t("vectorstoreAdmin.deletion.tooMany", {
                requested: formatNumber(problem.requested),
                limit: formatNumber(problem.limit),
              })
            : t("vectorstoreAdmin.deletion.failed")
        );
      },
    });
  };

  return (
    <Dialog
      open={deletion !== null}
      onCancel={onClose}
      afterClose={() => setShown(null)}
      onOk={handleDelete}
      title={t(
        shown?.selection.kind === "document"
          ? "vectorstoreAdmin.deletion.titleOne"
          : "vectorstoreAdmin.deletion.titleMany"
      )}
      okText={
        urls.length > 0
          ? t("vectorstoreAdmin.deletion.confirm", { count: urls.length, formattedCount: formatNumber(urls.length) })
          : t("vectorstoreAdmin.deletion.confirmNone")
      }
      okButtonProps={{ danger: true, disabled: urls.length === 0 }}
      // The delete runs to its end once sent: leaving would only hide how it ended
      cancelButtonProps={{ disabled: isPending }}
      confirmLoading={isPending}
      width={560}
    >
      {shown && <SelectionSummary selection={shown.selection} />}
      {isLoading && (
        <div className="flex items-center gap-3 py-6">
          <Spin spinning />
          <Text tone="muted">{t("vectorstoreAdmin.deletion.loading")}</Text>
        </div>
      )}
      {isError && <CorpusErrorAlert error={error} />}
      {preview &&
        (urls.length === 0 ? (
          <Alert type="info" showIcon message={t("vectorstoreAdmin.deletion.nothing")} />
        ) : (
          <PreviewSummary preview={preview} />
        ))}
    </Dialog>
  );
}
